// hooks/useRealtime.ts
import { useEffect, useCallback, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../../features/auth/contexts/AuthContext';
import { useUserProfile } from './useUserProfile';
import { debounce } from 'lodash';

interface RealtimeConfig {
  table: string;
  teamId?: string;
  projectId?: string;
  onInsert?: (payload: any) => void;
  onUpdate?: (payload: any) => void;
  onDelete?: (payload: any) => void;
  onPresenceChange?: (users: any[]) => void;
}

export function useRealtime({
  table,
  teamId,
  projectId,
  onInsert,
  onUpdate,
  onDelete,
  onPresenceChange
}: RealtimeConfig) {
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const channelRef = useRef<any>(null);
  const presenceChannelRef = useRef<any>(null);

  // Determine the ID and filter column to use
  const filterId = projectId || teamId;
  const filterColumn = projectId ? 'project_id' : 'team_id';

  // Set up real-time subscription for data changes
  useEffect(() => {
    if (!filterId) return;

    console.log(`🔄 Setting up real-time subscription for table: ${table}, ${filterColumn}: ${filterId}`);

    // Create channel for data changes
    const channel = supabase
      .channel(`${table}_changes_${filterId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: table,
        filter: `${filterColumn}=eq.${filterId}`
      }, (payload) => {
        console.log('📝 Real-time INSERT:', payload);
        onInsert?.(payload);
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: table,
        filter: `${filterColumn}=eq.${filterId}`
      }, (payload) => {
        console.log('✏️ Real-time UPDATE:', payload);
        onUpdate?.(payload);
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: table,
        filter: `${filterColumn}=eq.${filterId}`
      }, (payload) => {
        console.log('🗑️ Real-time DELETE:', payload);
        onDelete?.(payload);
      })
      .subscribe((status) => {
        console.log(`📡 Subscription status for ${table}:`, status);
      });

    channelRef.current = channel;

    return () => {
      console.log(`🔌 Unsubscribing from ${table} changes`);
      supabase.removeChannel(channel);
    };
  }, [table, filterId, filterColumn, onInsert, onUpdate, onDelete]);

  // Set up presence tracking
  useEffect(() => {
    if (!filterId || !user) return;

    console.log(`👥 Setting up presence for table: ${table}, ${filterColumn}: ${filterId}`);

    const presenceChannel = supabase
      .channel(`${table}_presence_${filterId}`)
      .on('presence', { event: 'sync' }, () => {
        const state = presenceChannel.presenceState();
        const users = Object.values(state).flat();
        console.log('👥 Presence sync:', users);
        onPresenceChange?.(users);
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        console.log('👋 User joined:', key, newPresences);
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        console.log('👋 User left:', key, leftPresences);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          // Track this user's presence
          await presenceChannel.track({
            userId: user.id,
            userName: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || user.email,
            userEmail: user.email,
            avatar: (profile as any)?.avatar_url || undefined,
            lastSeen: new Date().toISOString(),
            editingTable: table,
            contextId: filterId,
            contextType: projectId ? 'project' : 'team'
          });
        }
      });

    presenceChannelRef.current = presenceChannel;

    return () => {
      console.log(`🔌 Unsubscribing from ${table} presence`);
      supabase.removeChannel(presenceChannel);
    };
  }, [table, filterId, filterColumn, user, profile, onPresenceChange, projectId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
      if (presenceChannelRef.current) {
        supabase.removeChannel(presenceChannelRef.current);
      }
    };
  }, []);
}

// Autosave hook
export function useAutosave<T>(
  data: T,
  saveFunction: (data: T) => Promise<void>,
  delay: number = 1000
) {
  const debouncedSave = useCallback(
    debounce(async (dataToSave: T) => {
      try {
        console.log('💾 Autosaving data...');
        await saveFunction(dataToSave);
        console.log('✅ Autosave successful');
      } catch (error) {
        console.error('❌ Autosave failed:', error);
      }
    }, delay),
    [saveFunction, delay]
  );

  const lastDataRef = useRef<T>(data);

  useEffect(() => {
    // Only autosave if data has actually changed
    if (JSON.stringify(data) !== JSON.stringify(lastDataRef.current)) {
      debouncedSave(data);
      lastDataRef.current = data;
    }
  }, [data, debouncedSave]);

  // Cleanup
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);
}

// Additional helper hook for real-time document collaboration
export function useDocumentCollaboration(
  documentId: string,
  table: string,
  projectId?: string,
  teamId?: string
) {
  const [isOnline, setIsOnline] = useState(true);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [collaborators, setCollaborators] = useState<any[]>([]);

  const saveDocument = useCallback(async (data: any) => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from(table)
        .update({
          ...data,
          updated_at: new Date().toISOString(),
          updated_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', documentId);

      if (error) throw error;
      setLastSaved(new Date());
    } catch (error) {
      console.error('Failed to save document:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [documentId, table]);

  // Set up real-time collaboration
  useRealtime({
    table,
    projectId,
    teamId,
    onUpdate: (payload) => {
      if (payload.new.id === documentId) {
        console.log('Document updated by collaborator:', payload);
        // Handle the update - could emit an event or call a callback
        window.dispatchEvent(new CustomEvent('documentUpdated', { 
          detail: payload.new 
        }));
      }
    },
    onPresenceChange: setCollaborators
  });

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return {
    saveDocument,
    isOnline,
    lastSaved,
    isSaving,
    collaborators
  };
}